import { labelOf } from './dimensions';
import type { MentionRow } from './generator';
import { mulberry32, pickWeighted } from './prng';
import { rowId } from './sentimentOverrides';

/**
 * Post breakdown — what the Competitors `Breakdown` drawer (2038:16752) shows
 * for one post: its comments, how they split by sentiment, the top few, and
 * its reactions by type.
 *
 * **Most of this is synthesized fixture content, and deliberately so** (settled
 * with the user 2026-09-18). The corpus is structured mentions: a post carries
 * comment, share, reaction and like *counts*, but no individual comments, no
 * comment text, no notion of relevance, and no reaction types beyond "like"
 * and "other". Everything the corpus does not hold is generated here — and
 * everything it does hold is used as-is, so the drawer can never disagree with
 * the table that opened it:
 *
 *   Total comments   = the post's own `comments`                  (real)
 *   Total shares     = the post's own `shares`                    (real)
 *   Reactions total  = the post's own `likes + reactions`         (real)
 *   Relevant comments, their sentiment, the top comments,
 *   and the split of reactions into types                        (synthesized)
 *
 * **Deterministic per post.** Each post draws from its own PRNG stream, seeded
 * by its corpus id, so the same post always opens to the same breakdown — and
 * the stream is separate from the corpus generator's, so adding this changed
 * nothing about any existing figure. The only input that can move a post's
 * breakdown is its sentiment: comment sentiment leans on the post's, and a Tune
 * sentiment correction is the post's sentiment from then on.
 */

/** Separate from the corpus seed, so this stream cannot perturb the corpus. */
const BREAKDOWN_SEED = 0x0b8e_a4d0;

/** Figma's table draws five rows and no pagination. */
export const TOP_COMMENT_COUNT = 5;

export type SentimentKey = 'positive' | 'neutral' | 'negative';

export type TopComment = {
  id: string;
  text: string;
  reactions: number;
  sentiment: SentimentKey;
};

export type ReactionCount = { type: string; count: number };

export type PostBreakdown = {
  totalComments: number;
  /**
   * Comments judged to be about the post's subject rather than spam, tags or
   * off-topic replies. Synthesized: 55–90% of the total, per post.
   */
  relevantComments: number;
  totalShares: number;
  /** The relevant comments by sentiment, in the chips' order. Sums to `relevantComments`. */
  commentSentiment: Readonly<Record<SentimentKey, number>>;
  /** Up to five, highest-reacted first. */
  topComments: TopComment[];
  /**
   * The post's reactions by type, in the platform's own order. Sums to the
   * post's `likes + reactions` exactly.
   */
  reactions: ReactionCount[];
  /** `likes + reactions` — the figure `reactions` adds up to. */
  totalReactions: number;
};

/* ------------------------------------------------------------ reaction sets */

/**
 * Each platform's own reaction set, in its own order (settled with the user:
 * "each platform's own set"). A platform with a single reaction gets a
 * one-bar chart, which is the truthful picture of it.
 *
 * Like is the post's own `likes`; the rest split the post's `reactions`. On a
 * single-reaction platform there is nothing to split into, so the corpus's
 * `reactions` fold into the one type — which keeps the total reconciled with
 * the table, the same fold `data/pages.ts` makes.
 */
type ReactionSet = { like: string; others: readonly string[]; weights?: Record<SentimentKey, readonly number[]> };

const REACTION_SETS: Readonly<Record<string, ReactionSet>> = {
  facebook: {
    like: 'Like',
    others: ['Love', 'Care', 'Haha', 'Wow', 'Sad', 'Angry'],
    /* How each reaction leans with the post's sentiment. Invented, and plausible. */
    weights: {
      positive: [0.45, 0.15, 0.2, 0.12, 0.04, 0.04],
      neutral: [0.2, 0.1, 0.25, 0.3, 0.08, 0.07],
      negative: [0.05, 0.12, 0.13, 0.15, 0.25, 0.3],
    },
  },
  linkedin: {
    like: 'Like',
    others: ['Celebrate', 'Support', 'Love', 'Insightful', 'Funny'],
    weights: {
      positive: [0.35, 0.2, 0.2, 0.18, 0.07],
      neutral: [0.15, 0.15, 0.1, 0.5, 0.1],
      negative: [0.05, 0.45, 0.05, 0.4, 0.05],
    },
  },
  /* Reddit's single reaction is the upvote, not a like. */
  reddit: { like: 'Upvote', others: [] },
};

/** X, YouTube, TikTok and Instagram: likes only. */
const LIKES_ONLY: ReactionSet = { like: 'Like', others: [] };

/* ------------------------------------------------------- comment sentiment */

/** How the comments under a post lean, by the post's own sentiment. */
const COMMENT_LEAN: Readonly<Record<SentimentKey, readonly number[]>> = {
  positive: [0.62, 0.24, 0.14],
  neutral: [0.3, 0.46, 0.24],
  negative: [0.12, 0.22, 0.66],
};

const SENTIMENTS: readonly SentimentKey[] = ['positive', 'neutral', 'negative'];

/* ---------------------------------------------------------- comment copy */

/**
 * Comment templates by sentiment. `{topic}` and `{brand}` are filled from the
 * post. Invented fixture copy, written to read like a Bangladeshi telco's
 * comment section. `{topic}` always sits mid-sentence — see `midSentence`.
 *
 * **No template conjugates a verb against `{topic}`.** Several topic labels are
 * plural — Sponsorships, Promotions, Data packs, Device offers, Loyalty
 * rewards, Contract terms — so "{Topic} has been terrible" came out as
 * "Sponsorships has been terrible" in the first build. Every line is phrased so
 * the topic is an object or a modifier, never the subject, and reads correctly
 * for both.
 */
const TEMPLATES: Readonly<Record<SentimentKey, readonly string[]>> = {
  positive: [
    'Honestly impressed with the {topic} lately. Keep it up {brand}.',
    'No complaints about {topic} this week, all smooth.',
    'Switched for the {topic} and it was worth it.',
    'Good to see {brand} finally sorting out {topic}.',
  ],
  neutral: [
    'Does anyone know if the {topic} change applies to prepaid too?',
    'Same {topic} deal in every division, or just Dhaka?',
    "What's the difference between this and the old {topic}?",
    'Waiting to see how {brand} handles the new {topic}.',
  ],
  negative: [
    'Nothing but trouble with {topic} since last week. Fix it {brand}.',
    'Third complaint about {topic} and still no reply.',
    'Paying more and getting worse {topic}. Not happy.',
    'Anyone else having {topic} issues today?',
  ],
};

/** A label mid-sentence: lowercase the first letter unless it opens an acronym (`SIM activation`). */
function midSentence(label: string): string {
  return label.length > 1 && label[1] === label[1].toLowerCase()
    ? label[0].toLowerCase() + label.slice(1)
    : label;
}

/* ------------------------------------------------------------------ split */

/**
 * `total` split by `weights` into whole numbers that add back to exactly
 * `total` — largest remainder, the same method the corpus generator uses for
 * engagement types. Rounding each share independently would drift the sum.
 */
function split(total: number, weights: readonly number[]): number[] {
  const sum = weights.reduce((a, b) => a + b, 0);
  if (total <= 0 || sum <= 0) return weights.map(() => 0);
  const exact = weights.map((w) => (w / sum) * total);
  const out = exact.map(Math.floor);
  let remainder = total - out.reduce((a, b) => a + b, 0);
  const order = exact
    .map((value, i) => ({ i, frac: value - Math.floor(value) }))
    .sort((a, b) => b.frac - a.frac);
  for (let k = 0; remainder > 0; k += 1, remainder -= 1) out[order[k % order.length].i] += 1;
  return out;
}

/** Each weight nudged by ±20%, so a lean is a tendency and not a constant. */
function jitter(rand: () => number, weights: readonly number[]): number[] {
  return weights.map((w) => w * (0.8 + rand() * 0.4));
}

/* ------------------------------------------------------------------- build */

export function postBreakdown(row: MentionRow): PostBreakdown {
  const id = rowId(row);
  /* One stream per post: seeded by its id, mixed so neighbouring ids diverge at once. */
  const rand = mulberry32((BREAKDOWN_SEED ^ Math.imul(id + 1, 0x9e37_79b1)) >>> 0);
  const sentiment = (SENTIMENTS as readonly string[]).includes(row.sentiment)
    ? (row.sentiment as SentimentKey)
    : 'neutral';

  /* Comments ------------------------------------------------------------ */

  const relevance = 0.55 + rand() * 0.35;
  const relevantComments = Math.round(row.comments * relevance);
  const [positive, neutral, negative] = split(relevantComments, jitter(rand, COMMENT_LEAN[sentiment]));
  const commentSentiment = { positive, neutral, negative };

  /* Top comments --------------------------------------------------------- */

  const brand = labelOf('brand', row.brand);
  const topic = labelOf('topic', row.topic);
  const used: Record<SentimentKey, Set<number>> = { positive: new Set(), neutral: new Set(), negative: new Set() };

  /*
   * The top comment draws 2–8% of the post's own likes; each after it a
   * steadily smaller share. Comment reactions are not part of the post's own
   * reaction count, so these are not reconciled against it — only kept on a
   * plausible scale beside it.
   */
  let reactions = Math.max(1, Math.round(row.likes * (0.02 + rand() * 0.06)));
  const topComments: TopComment[] = [];
  for (let i = 0; i < Math.min(TOP_COMMENT_COUNT, relevantComments); i += 1) {
    /* Drawn from the comments' own sentiment split, so the top five resemble the whole. */
    const s = SENTIMENTS[pickWeighted(rand, [positive, neutral, negative])];
    const pool = TEMPLATES[s];
    /* Without repeats while the pool allows, so two rows never read the same. */
    let t = Math.floor(rand() * pool.length);
    for (let tries = 0; used[s].has(t) && tries < pool.length; tries += 1) t = (t + 1) % pool.length;
    used[s].add(t);

    topComments.push({
      id: `${id}:${i}`,
      text: pool[t].replace('{topic}', midSentence(topic)).replace('{brand}', brand),
      reactions,
      sentiment: s,
    });
    reactions = Math.max(0, Math.floor(reactions * (0.5 + rand() * 0.35)));
  }

  /* Reactions by type ---------------------------------------------------- */

  const set = REACTION_SETS[row.channel] ?? LIKES_ONLY;
  const totalReactions = row.likes + row.reactions;
  const reactionCounts: ReactionCount[] =
    set.others.length === 0 || !set.weights
      ? [{ type: set.like, count: totalReactions }]
      : [
          { type: set.like, count: row.likes },
          ...split(row.reactions, jitter(rand, set.weights[sentiment])).map((count, i) => ({
            type: set.others[i],
            count,
          })),
        ];

  return {
    totalComments: row.comments,
    relevantComments,
    totalShares: row.shares,
    commentSentiment,
    topComments,
    reactions: reactionCounts,
    totalReactions,
  };
}

/* ------------------------------------------------------------- AI summary */

const summaryWhole = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

/**
 * The paragraph the breakdown panels' `AI summary` card prints — Figma's
 * `Streaming Text` layer inside `Cards/Card` 2038:29327.
 *
 * **Figures only, never causes.** The house rule for every summary in the app,
 * and here it is not a style choice: this module can count comments, reactions
 * and shares, and it cannot know why any of them were written. The frame's
 * example copy reaches for causes ("The cause is a terminal congestion
 * incident…") because it is lorem for a different product; writing that shape
 * against this corpus would mean inventing the reason.
 *
 * Lives in the data layer rather than in the view, so the drawer and the modal
 * cannot drift into wording the other one does not have. Four lines at 542
 * wide, which is the height the frame's card is drawn for.
 *
 * **Not generated by a model, and not hedged as though it were.** It is a
 * template over figures already on screen, which is why it can be stated
 * flatly. Expect the user to replace it when the breakdown's logic lands.
 */
export function breakdownSummary(b: PostBreakdown, noun: 'post' | 'conversation' = 'post'): string {
  const n = summaryWhole.format.bind(summaryWhole);

  if (b.relevantComments === 0) {
    return `No relevant comments on this ${noun} yet, so there is nothing to break down by sentiment. It carries **${n(b.totalReactions)} reactions** and **${n(b.totalShares)} shares**.`;
  }

  const share = (s: SentimentKey) => (b.commentSentiment[s] / b.relevantComments) * 100;
  const leading = SENTIMENTS.reduce((best, s) =>
    b.commentSentiment[s] > b.commentSentiment[best] ? s : best,
  );
  const net = share('positive') - share('negative');
  const top = [...b.reactions].sort((x, y) => y.count - x.count)[0];
  const relevance = (b.relevantComments / b.totalComments) * 100;

  return [
    `**${n(b.relevantComments)} of ${n(b.totalComments)} comments** on this ${noun} are about its subject — ${relevance.toFixed(0)}% of them.`,
    `**${labelOf('sentiment', leading)}** leads at ${share(leading).toFixed(1)}%, putting net sentiment at **${net > 0 ? '+' : ''}${net.toFixed(1)}**.`,
    top && b.totalReactions > 0
      ? `Of ${n(b.totalReactions)} reactions, **${top.type}** is the most common.`
      : `It has drawn no reactions.`,
    `The ${noun} has been shared **${n(b.totalShares)}** times.`,
  ].join(' ');
}
